import { Dispatch, SetStateAction, useState } from "react"
import { Stack } from "@mui/material"
import { ConfigQuery, fetchResourceFilters, ResourceFiltersResponse } from "../../../createOrderApi"
import {
    ResourceFilterElementValue,
    ResourceFiltersSelectedItem,
    ResourceFiltersTypes,
} from "../../../createOrderTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { AddFiltersButton } from "./AddFiltersButton"
import { FilterChip } from "./FilterChip"

const TIMELINE_EXCLUDED_GROUP_KEYS = ["key_group_2", "key_group_3"] as const

const transformFilters = (data: ResourceFiltersResponse, isTimeline: boolean) => {
    return (data.filterGroups ?? []).flatMap((group) => {
        if (isTimeline && TIMELINE_EXCLUDED_GROUP_KEYS.includes(group.translationKey as (typeof TIMELINE_EXCLUDED_GROUP_KEYS)[number])) {
            return []
        }
        const elements = group.filterElements ?? []
        return group.translationKey === "key_group_1"
            ? elements.map((el) => ({ ...el, name: group.name }))
            : elements
    })
}

type ResourceFilterMenuProps = Readonly<{
    filters: ResourceFiltersSelectedItem[]
    setFilters: Dispatch<SetStateAction<ResourceFiltersTypes>>
    isTimeline?: boolean
    userId: number
}>

export function ResourceFilterMenu({ filters, userId, setFilters, isTimeline = false }: ResourceFilterMenuProps) {
    const { data: resourceFiltersResult = [] } = useApiQuery(fetchResourceFilters, {
        queryName: ConfigQuery.RESOURCE_FILTERS,
        dependencies: {
            userId,
        },
        select: (data) => transformFilters(data, isTimeline),
    })

    const [activeFilter, setActiveFilter] = useState<string | null>(null)

    const handleAddFilter = (filter: string) => {
        setFilters((prev) => {
            if (prev.filters.some((f) => f.identifier === filter)) {
                return { ...prev, filters: prev.filters.filter((f) => f.identifier !== filter) }
            }
            return { ...prev, filters: [...prev.filters, { identifier: filter, values: [] }] }
        })
        setActiveFilter(filter)
    }

    const updateFilterValues = (identifier: string, values: ResourceFilterElementValue[]) => {
        setFilters((prev) => {
            return {
                ...prev,
                filters: prev.filters.map((filter) =>
                    filter.identifier === identifier ? { ...filter, values } : filter,
                ),
            }
        })
    }

    return (
        <Stack flexDirection="row" gap=".75rem" flexWrap="wrap" width="fit-content" alignItems="center" flex="none">
            {filters.map((filter) => (
                <FilterChip
                    key={filter.identifier}
                    filterType={filter.identifier}
                    isRemovable={filter?.isRemovable}
                    onRemove={() =>
                        setFilters((prev) => {
                            return { ...prev, filters: prev.filters.filter((f) => f !== filter) }
                        })
                    }
                    resourceFiltersResult={resourceFiltersResult}
                    isActive={activeFilter === filter.identifier}
                    setActiveFilter={setActiveFilter}
                    initialValues={filter.values}
                    onSave={(values) => updateFilterValues(filter.identifier, values)}
                />
            ))}

            <AddFiltersButton
                filters={filters}
                resourceFiltersResult={resourceFiltersResult}
                onAddFilter={handleAddFilter}
            />
        </Stack>
    )
}
